// Issue v2 (SW-1198, base path /issue/v2)
//
// v1 `Issue`(/shipping/v1/issues)와 테이블·계약이 분리된 독립 도메인이다. 확장이 아니므로
// 타입도 공유하지 않는다.
//
// 주의: Tracker 클라이언트는 `applyCaseMiddleware` 를 옵션 없이 적용하므로 응답 키가
// 자동 camelCase 로 변환된다. 이 변환은 **enum 코드를 키로 쓰는 맵**에도 적용되어
// `total_count.INCIDENT_IN_PROGRESS` 는 FE 에 `totalCount.incidentInProgress` 로 도착한다.
// 카운트 맵 타입(`IssueV2StateCountMap` 등)이 camelCase 인 이유다.

export type IssueV2State =
  | "PENDING"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CANCELED"
  | "INCIDENT_PENDING"
  | "INCIDENT_IN_PROGRESS"
  | "INCIDENT_DECLARED"
  | "COMPENSATION_COMPLETED"
  | "INCIDENT_CANCELED"

export type IssueV2Category =
  | "DELIVERY"
  | "COLLECTION"
  | "SHIPPING_ACCIDENT"
  | "POINT_SETTLEMENT"
  | "SYSTEM"
  | "SERVICE"
  | "ETC"

export type IssueV2Subcategory =
  // DELIVERY
  | "WRONG_DELIVERY"
  | "NOT_RECEIVED"
  | "FALSE_DELIVERY"
  | "DELIVERY_DELAYED"
  | "UNAUTHORIZED_DELIVERY"
  | "UNAUTHORIZED_RETURN"
  | "DELIVERY_STOP_OR_RETURN_REQUEST"
  | "DELIVERY_SCHEDULE_INQUIRY"
  | "ADDRESS_CHANGE"
  | "ETC_DELIVERY_INQUIRY"
  // COLLECTION
  | "WRONG_COLLECTION"
  | "COLLECTION_DELAYED"
  | "COLLECTION_MISSED"
  | "COLLECTION_STOP_REQUEST"
  | "COLLECTION_SCHEDULE_INQUIRY"
  | "ETC_COLLECTION_INQUIRY"
  // SHIPPING_ACCIDENT
  | "LOST"
  | "DAMAGED"
  | "CONTAMINATED"
  | "ACCIDENT_COMPENSATION_INQUIRY"
  | "ETC_ACCIDENT_INQUIRY"
  // POINT_SETTLEMENT
  | "POINT_SETTLEMENT_INQUIRY"
  | "POINT_REFUND_INQUIRY"
  | "POINT_SETTLEMENT_MISMATCH"
  | "ETC_POINT_SETTLEMENT_INQUIRY"
  // SYSTEM
  | "INVOICE_INFO_ERROR"
  | "SCAN_MISSED"
  | "REASON_MISSED"
  | "ETC_SYSTEM_INQUIRY"
  // SERVICE
  | "CS_COMPLAINT"
  | "DRIVER_COMPLAINT"
  | "ETC_SERVICE_INQUIRY"
  // ETC
  | "ETC_INQUIRY"

export type IssueV2IncidentCategory = "LOST" | "DAMAGED" | "DELAYED" | "ETC"

export type IssueV2IncidentDetail =
  // LOST
  | "LOST_BEFORE_TRANSPORT"
  | "LOST_IN_SORTING"
  | "LOST_BY_MISSORTING"
  | "LOST_NOT_RETURNED"
  | "LOST_IN_DELIVERY"
  | "LOST_BY_WRONG_DELIVERY"
  | "LOST_BY_UNAUTHORIZED_DELIVERY"
  | "LOST_BY_MISSING_SCAN"
  // DAMAGED
  | "DAMAGED_BEFORE_TRANSPORT"
  | "DAMAGED_IN_SORTING"
  | "DAMAGED_BY_MISSORTING"
  | "DAMAGED_IN_DELIVERY"
  | "DAMAGED_BY_WRONG_DELIVERY"
  | "DAMAGED_BY_UNAUTHORIZED_DELIVERY"
  | "DAMAGED_AFTER_DELIVERY"
  // DELAYED
  | "DELIVERY_DELAYED_PLAIN"
  | "DELIVERY_DELAYED_BY_MISSORTING"
  // ETC
  | "INCIDENT_UNAUTHORIZED_DELIVERY"
  | "INCIDENT_UNAUTHORIZED_RETURN"
  | "INCIDENT_WRONG_COLLECTION"
  | "INCIDENT_ETC"

export type IssueV2IncidentCancelReason = "MISFILED" | "CLARIFICATION_ACCEPTED"

export type IssueV2FaultTargetType = "GROUP" | "CLIENT"

export type IssueV2PenaltyStage = "NONE" | "FIRST" | "SECOND"

export type IssueGroupType =
  | "TO_DAY"
  | "CUSTOMER_CENTER"
  | "MASTER"
  | "ORGANIZATION"

export type IssueV2EventType =
  | "STATE_CHANGED"
  | "ASSIGNEE_CHANGED"
  | "WATCHER_ADDED"
  | "WATCHER_REMOVED"
  | "CATEGORY_CHANGED"
  | "INCIDENT_ENTERED"
  | "INCIDENT_FIELD_CHANGED"
  | "REOPENED"

// Resources

export interface IssueV2Incident {
  category: IssueV2IncidentCategory
  detail: IssueV2IncidentDetail
  cancelReason: IssueV2IncidentCancelReason | null
  responseDueTime: string | null
}

export interface IssueV2FaultLabel {
  targetType: IssueV2FaultTargetType
  /** `GROUP` 이면 그룹 UUID, `CLIENT` 면 12자리 client_id */
  targetId: string
  /** null = 금액 미정 (사고확정 단계에서만 허용). 배상확정 시 전 항목 필수 */
  amount: number | null
}

export interface IssueV2Comment {
  id: string
  authorGroupId: string
  authorUserId: string
  authorName: string
  content: string
  attachmentUrls: string[]
  createTime: string
}

/**
 * 이벤트 `from`/`to` 의 공통 payload. type 별로 채워지는 키가 다르고,
 * `INCIDENT_FIELD_CHANGED` 는 변경된 필드만 임의 조합으로 담는다(api-spec §3.4).
 */
export interface IssueV2EventPayload {
  state?: IssueV2State
  assigneeGroupId?: string
  watcherGroupId?: string
  category?: IssueV2Category
  subcategory?: IssueV2Subcategory
  incidentCategory?: IssueV2IncidentCategory
  incidentDetail?: IssueV2IncidentDetail
  incidentResponseDueTime?: string
  cancelReason?: IssueV2IncidentCancelReason
  faultLabels?: IssueV2FaultLabel[]
  organizationShareDate?: string
  compensationCompleteTime?: string
  organizationDeductDate?: string
  clientDepositDate?: string
  nextIssueId?: string
}

export interface IssueV2Event {
  id: string
  type: IssueV2EventType
  eventTime: string
  actorGroupId: string
  actorUserId: string
  actorName: string
  from: IssueV2EventPayload | null
  to: IssueV2EventPayload | null
}

export interface IssueV2 {
  id: string
  loadId: string
  displayInvoiceNumber: string
  clientId: string
  clientName: string
  productName: string
  category: IssueV2Category
  subcategory: IssueV2Subcategory
  state: IssueV2State
  assigneeGroupId: string | null
  requesterGroupId: string | null
  watcherGroupIds: string[]
  /** 일반 답변기한. 등록 시 1회 입력, 이후 불변 */
  responseDueTime: string | null
  /** 사고 라인에 진입한 적 없으면 null. 사고취소 후에도 유지된다 */
  incident: IssueV2Incident | null
  /** 사고확정 전에는 빈 배열 */
  faultLabels: IssueV2FaultLabel[]
  incidentDeclareTime: string | null
  organizationShareDate: string | null
  compensationCompleteTime: string | null
  organizationDeductDate: string | null
  clientDepositDate: string | null
  /** 값이 있으면 재오픈으로 생성된 이슈 */
  prevIssueId: string | null
  /** 값이 있으면 재오픈되어 다음 이슈가 생긴 이슈 */
  nextIssueId: string | null
  /** 조회 시점 계산값 (api-spec §7) */
  penaltyStage: IssueV2PenaltyStage
  /** 조회 시점 계산값 (api-spec §7) */
  clarificationOverdue: boolean
  createTime: string
  lastUpdateTime: string
  /** 목록은 `include_comments=true` 일 때만 포함된다 (false면 키 자체가 없음) */
  comments?: IssueV2Comment[]
  /** 목록은 `include_events=true` 일 때만 포함된다 */
  events?: IssueV2Event[]
}

/** 상세 조회(`GET /issues/{id}`)와 쓰기 응답은 comments·events 를 항상 포함한다 */
export type IssueV2Detail = IssueV2 & {
  comments: IssueV2Comment[]
  events: IssueV2Event[]
}

export interface IssueGroup {
  id: string
  type: IssueGroupType
  name: string
  /** false 면 신규 지정 대상에서 숨긴다 (이력 렌더링에는 계속 사용) */
  active: boolean
}

// Request types

export interface CreateIssueV2CommentRequest {
  content: string
  attachmentUrls?: string[]
}

export interface CreateIssueV2Request {
  category: IssueV2Category
  subcategory: IssueV2Subcategory
  /** 생략 = 메모모드 → BE 가 즉시 COMPLETED 로 시작시킨다 */
  assigneeGroupId?: string
  watcherGroupIds?: string[]
  /** 담당 그룹이 협력사(ORGANIZATION)일 때만 허용. 위반 시 400 E14009 */
  responseDueTime?: string
  /** 최소 1개. 본문 = 첫 코멘트 */
  comments: CreateIssueV2CommentRequest[]
  /** 값이 있으면 재오픈 */
  prevIssueId?: string
}

/** PATCH 의 `incident` 는 하위 필드도 부분 반영된다 (category·detail 은 쌍으로) */
export interface UpdateIssueV2IncidentRequest {
  category?: IssueV2IncidentCategory
  detail?: IssueV2IncidentDetail
  cancelReason?: IssueV2IncidentCancelReason
  responseDueTime?: string
}

/** 보낸 필드만 반영된다. `responseDueTime` 은 PATCH 로 보낼 수 없다 (400 E14009) */
export interface UpdateIssueV2Request {
  state?: IssueV2State
  /** null 불허 — 담당 해제 시나리오가 없다 */
  assigneeGroupId?: string
  category?: IssueV2Category
  subcategory?: IssueV2Subcategory
  /** 전체 배열 교체. 빈 배열 = 참조 전부 해제 */
  watcherGroupIds?: string[]
  incident?: UpdateIssueV2IncidentRequest
  /** 전체 배열 교체. 사고확정 이후 최소 1개 유지 */
  faultLabels?: IssueV2FaultLabel[]
  organizationShareDate?: string
  compensationCompleteTime?: string
  organizationDeductDate?: string
  clientDepositDate?: string
}

// Response types

export interface IssueV2StateCountMap {
  pending: number
  inProgress: number
  completed: number
  canceled: number
  incidentPending: number
  incidentInProgress: number
  incidentDeclared: number
  compensationCompleted: number
  incidentCanceled: number
}

export interface IssueV2PenaltyStageCountMap {
  none: number
  first: number
  second: number
}

export interface IssueV2StatsResponse {
  totalCount: IssueV2StateCountMap
  penaltyStageCount: IssueV2PenaltyStageCountMap
  clarificationOverdueCount: number
}

export interface ListIssueGroupsResponse {
  issueGroups: IssueGroup[]
}

/** api-spec §1.6 화이트리스트. 이 외 값은 400 */
export type IssueV2ListOrder =
  | "create_time.asc"
  | "create_time.desc"
  | "last_update_time.asc"
  | "last_update_time.desc"
  | "response_due_time.asc"
  | "response_due_time.desc"

// Labels
//
// 상태 한글명은 프로토타입 시안 표기를 따른다. api-spec 별첨 A.1 은
// INCIDENT_PENDING/INCIDENT_IN_PROGRESS 를 "사고발생-소명대기"/"사고발생-소명중" 으로
// 적었지만 시안의 탭·컬럼은 "소명대기"/"소명중" 으로 렌더링한다.

export const ISSUE_V2_STATE_LABELS: Record<IssueV2State, string> = {
  PENDING: "확인대기",
  IN_PROGRESS: "확인중",
  COMPLETED: "처리완료",
  CANCELED: "취소종결",
  INCIDENT_PENDING: "소명대기",
  INCIDENT_IN_PROGRESS: "소명중",
  INCIDENT_DECLARED: "사고확정",
  COMPENSATION_COMPLETED: "배상확정",
  INCIDENT_CANCELED: "사고취소",
}

export const ISSUE_V2_CATEGORY_LABELS: Record<IssueV2Category, string> = {
  DELIVERY: "배송",
  COLLECTION: "수거·반품",
  SHIPPING_ACCIDENT: "배송사고",
  POINT_SETTLEMENT: "포인트·정산",
  SYSTEM: "시스템",
  SERVICE: "서비스 평가",
  ETC: "기타·일반",
}

export const ISSUE_V2_SUBCATEGORY_LABELS: Record<IssueV2Subcategory, string> = {
  WRONG_DELIVERY: "오배송",
  NOT_RECEIVED: "미수령",
  FALSE_DELIVERY: "허위배송(허위스캔)",
  DELIVERY_DELAYED: "배송지연",
  UNAUTHORIZED_DELIVERY: "임의배송",
  UNAUTHORIZED_RETURN: "임의반송",
  DELIVERY_STOP_OR_RETURN_REQUEST: "배송 중단·반송 요청",
  DELIVERY_SCHEDULE_INQUIRY: "배송 일정 문의·변경",
  ADDRESS_CHANGE: "배송지 변경",
  ETC_DELIVERY_INQUIRY: "기타 배송 문의",
  WRONG_COLLECTION: "오회수",
  COLLECTION_DELAYED: "수거 지연",
  COLLECTION_MISSED: "수거 누락",
  COLLECTION_STOP_REQUEST: "수거 중단 요청",
  COLLECTION_SCHEDULE_INQUIRY: "수거 일정 문의·변경",
  ETC_COLLECTION_INQUIRY: "기타 수거 문의",
  LOST: "분실",
  DAMAGED: "파손",
  CONTAMINATED: "오염·변질",
  ACCIDENT_COMPENSATION_INQUIRY: "사고 배상 문의",
  ETC_ACCIDENT_INQUIRY: "기타 사고 문의",
  POINT_SETTLEMENT_INQUIRY: "포인트·정산 내역 문의",
  POINT_REFUND_INQUIRY: "포인트 반환 문의",
  POINT_SETTLEMENT_MISMATCH: "포인트·정산 내역 불일치",
  ETC_POINT_SETTLEMENT_INQUIRY: "기타 포인트·정산 문의",
  INVOICE_INFO_ERROR: "송장 정보 오류",
  SCAN_MISSED: "스캔 누락(보류·중단·완료)",
  REASON_MISSED: "사유 누락(보류·중단)",
  ETC_SYSTEM_INQUIRY: "기타 시스템 문의",
  CS_COMPLAINT: "상담 응대 불만",
  DRIVER_COMPLAINT: "기사 응대 불만",
  ETC_SERVICE_INQUIRY: "기타 서비스 문의",
  ETC_INQUIRY: "기타 문의",
}

export const ISSUE_V2_INCIDENT_CATEGORY_LABELS: Record<
  IssueV2IncidentCategory,
  string
> = {
  LOST: "분실",
  DAMAGED: "파손",
  DELAYED: "배송지연",
  ETC: "기타",
}

export const ISSUE_V2_INCIDENT_DETAIL_LABELS: Record<
  IssueV2IncidentDetail,
  string
> = {
  LOST_BEFORE_TRANSPORT: "운송전 분실",
  LOST_IN_SORTING: "분류중 분실",
  LOST_BY_MISSORTING: "오분류 분실",
  LOST_NOT_RETURNED: "미반납 분실",
  LOST_IN_DELIVERY: "배송중 분실",
  LOST_BY_WRONG_DELIVERY: "오배송 분실",
  LOST_BY_UNAUTHORIZED_DELIVERY: "임의배송 분실",
  LOST_BY_MISSING_SCAN: "미스캔 분실",
  DAMAGED_BEFORE_TRANSPORT: "운송전 파손",
  DAMAGED_IN_SORTING: "분류중 파손",
  DAMAGED_BY_MISSORTING: "오분류 파손",
  DAMAGED_IN_DELIVERY: "배송중 파손",
  DAMAGED_BY_WRONG_DELIVERY: "오배송 파손",
  DAMAGED_BY_UNAUTHORIZED_DELIVERY: "임의배송 파손",
  DAMAGED_AFTER_DELIVERY: "배송후 파손",
  DELIVERY_DELAYED_PLAIN: "배송지연",
  DELIVERY_DELAYED_BY_MISSORTING: "오분류 배송지연",
  INCIDENT_UNAUTHORIZED_DELIVERY: "임의배송",
  INCIDENT_UNAUTHORIZED_RETURN: "임의반송",
  INCIDENT_WRONG_COLLECTION: "오회수",
  INCIDENT_ETC: "기타",
}

export const ISSUE_V2_INCIDENT_CANCEL_REASON_LABELS: Record<
  IssueV2IncidentCancelReason,
  string
> = {
  MISFILED: "오등록",
  CLARIFICATION_ACCEPTED: "소명승인",
}

export const ISSUE_V2_FAULT_TARGET_TYPE_LABELS: Record<
  IssueV2FaultTargetType,
  string
> = {
  GROUP: "그룹",
  CLIENT: "화주사",
}

export const ISSUE_V2_PENALTY_STAGE_LABELS: Record<
  IssueV2PenaltyStage,
  string
> = {
  NONE: "없음",
  FIRST: "1차",
  SECOND: "2차",
}

export const ISSUE_GROUP_TYPE_LABELS: Record<IssueGroupType, string> = {
  TO_DAY: "본사CS",
  CUSTOMER_CENTER: "고객센터",
  MASTER: "정류소",
  ORGANIZATION: "협력사",
}

// Membership maps — 등록·정정 폼의 2단 Select 원천 (소속 정합은 BE 도 검증한다)

export const ISSUE_V2_SUBCATEGORIES_BY_CATEGORY: Record<
  IssueV2Category,
  IssueV2Subcategory[]
> = {
  DELIVERY: [
    "WRONG_DELIVERY",
    "NOT_RECEIVED",
    "FALSE_DELIVERY",
    "DELIVERY_DELAYED",
    "UNAUTHORIZED_DELIVERY",
    "UNAUTHORIZED_RETURN",
    "DELIVERY_STOP_OR_RETURN_REQUEST",
    "DELIVERY_SCHEDULE_INQUIRY",
    "ADDRESS_CHANGE",
    "ETC_DELIVERY_INQUIRY",
  ],
  COLLECTION: [
    "WRONG_COLLECTION",
    "COLLECTION_DELAYED",
    "COLLECTION_MISSED",
    "COLLECTION_STOP_REQUEST",
    "COLLECTION_SCHEDULE_INQUIRY",
    "ETC_COLLECTION_INQUIRY",
  ],
  SHIPPING_ACCIDENT: [
    "LOST",
    "DAMAGED",
    "CONTAMINATED",
    "ACCIDENT_COMPENSATION_INQUIRY",
    "ETC_ACCIDENT_INQUIRY",
  ],
  POINT_SETTLEMENT: [
    "POINT_SETTLEMENT_INQUIRY",
    "POINT_REFUND_INQUIRY",
    "POINT_SETTLEMENT_MISMATCH",
    "ETC_POINT_SETTLEMENT_INQUIRY",
  ],
  SYSTEM: [
    "INVOICE_INFO_ERROR",
    "SCAN_MISSED",
    "REASON_MISSED",
    "ETC_SYSTEM_INQUIRY",
  ],
  SERVICE: ["CS_COMPLAINT", "DRIVER_COMPLAINT", "ETC_SERVICE_INQUIRY"],
  ETC: ["ETC_INQUIRY"],
}

export const ISSUE_V2_INCIDENT_DETAILS_BY_CATEGORY: Record<
  IssueV2IncidentCategory,
  IssueV2IncidentDetail[]
> = {
  LOST: [
    "LOST_BEFORE_TRANSPORT",
    "LOST_IN_SORTING",
    "LOST_BY_MISSORTING",
    "LOST_NOT_RETURNED",
    "LOST_IN_DELIVERY",
    "LOST_BY_WRONG_DELIVERY",
    "LOST_BY_UNAUTHORIZED_DELIVERY",
    "LOST_BY_MISSING_SCAN",
  ],
  DAMAGED: [
    "DAMAGED_BEFORE_TRANSPORT",
    "DAMAGED_IN_SORTING",
    "DAMAGED_BY_MISSORTING",
    "DAMAGED_IN_DELIVERY",
    "DAMAGED_BY_WRONG_DELIVERY",
    "DAMAGED_BY_UNAUTHORIZED_DELIVERY",
    "DAMAGED_AFTER_DELIVERY",
  ],
  DELAYED: ["DELIVERY_DELAYED_PLAIN", "DELIVERY_DELAYED_BY_MISSORTING"],
  ETC: [
    "INCIDENT_UNAUTHORIZED_DELIVERY",
    "INCIDENT_UNAUTHORIZED_RETURN",
    "INCIDENT_WRONG_COLLECTION",
    "INCIDENT_ETC",
  ],
}

// Stats 카운트 맵 키 매핑 — 응답 키가 camelCase 로 변환되므로 enum 코드로 직접 색인할 수 없다

export const ISSUE_V2_STATE_COUNT_KEYS: Record<
  IssueV2State,
  keyof IssueV2StateCountMap
> = {
  PENDING: "pending",
  IN_PROGRESS: "inProgress",
  COMPLETED: "completed",
  CANCELED: "canceled",
  INCIDENT_PENDING: "incidentPending",
  INCIDENT_IN_PROGRESS: "incidentInProgress",
  INCIDENT_DECLARED: "incidentDeclared",
  COMPENSATION_COMPLETED: "compensationCompleted",
  INCIDENT_CANCELED: "incidentCanceled",
}

export const ISSUE_V2_PENALTY_STAGE_COUNT_KEYS: Record<
  IssueV2PenaltyStage,
  keyof IssueV2PenaltyStageCountMap
> = {
  NONE: "none",
  FIRST: "first",
  SECOND: "second",
}

// Format functions

export function formatIssueV2State(state: IssueV2State): string {
  return ISSUE_V2_STATE_LABELS[state] ?? state
}

export function formatIssueV2Category(category: IssueV2Category): string {
  return ISSUE_V2_CATEGORY_LABELS[category] ?? category
}

export function formatIssueV2Subcategory(
  subcategory: IssueV2Subcategory
): string {
  return ISSUE_V2_SUBCATEGORY_LABELS[subcategory] ?? subcategory
}

export function formatIssueV2IncidentCategory(
  category: IssueV2IncidentCategory
): string {
  return ISSUE_V2_INCIDENT_CATEGORY_LABELS[category] ?? category
}

export function formatIssueV2IncidentDetail(
  detail: IssueV2IncidentDetail
): string {
  return ISSUE_V2_INCIDENT_DETAIL_LABELS[detail] ?? detail
}

export function formatIssueV2IncidentCancelReason(
  reason: IssueV2IncidentCancelReason
): string {
  return ISSUE_V2_INCIDENT_CANCEL_REASON_LABELS[reason] ?? reason
}

export function formatIssueV2FaultTargetType(
  targetType: IssueV2FaultTargetType
): string {
  return ISSUE_V2_FAULT_TARGET_TYPE_LABELS[targetType] ?? targetType
}

export function formatIssueV2PenaltyStage(stage: IssueV2PenaltyStage): string {
  return ISSUE_V2_PENALTY_STAGE_LABELS[stage] ?? stage
}

export function formatIssueGroupType(type: IssueGroupType): string {
  return ISSUE_GROUP_TYPE_LABELS[type] ?? type
}
